import type { ReactNode } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Wraps async query UI: spinner while loading, message on error, else children. */
export function DataState<T>({
  query,
  children,
  className,
}: {
  query: { isLoading: boolean; isError: boolean; error?: unknown; data?: T };
  children: (data: T) => ReactNode;
  className?: string;
}) {
  if (query.isLoading) {
    return (
      <div className={cn('flex items-center justify-center gap-2 py-20 text-ink-muted', className)}>
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Cargando datos…</span>
      </div>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 py-20 text-ink-muted', className)}>
        <TriangleAlert className="h-6 w-6 text-warning-text" />
        <span className="text-sm">No se pudieron cargar los datos.</span>
        <span className="text-xs text-ink-faint">{query.error instanceof Error ? query.error.message : 'Verifica la API / D1.'}</span>
      </div>
    );
  }
  return <>{children(query.data)}</>;
}

export function EmptyState({ message, icon }: { message: string; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-ink-faint">
      {icon}
      <span className="text-sm">{message}</span>
    </div>
  );
}

/**
 * Para lo que está CONSTRUIDO pero todavía SIN DATOS.
 *
 * ⚠️ Reemplazó a `ComingSoon`, que pintaba un cartel **«Próximamente · Fase 2»**. Ese cartel
 * tuvo sentido mientras el motor no existía; una vez que existió, siguió apareciendo en
 * pantallas que ya funcionaban y le decía al usuario que la feature no estaba hecha cuando
 * lo único que pasaba era que la tabla estaba vacía. Son dos cosas distintas y el usuario
 * merece saber cuál de las dos es: si es "no existe" no puede hacer nada, y si es "no hay
 * datos" casi siempre puede.
 *
 * Por eso `comoLlenarlo` es obligatorio: un estado vacío que no dice cómo dejar de estarlo
 * es una pared.
 */
export function SinDatos({
  title,
  detail,
  comoLlenarlo,
}: {
  title: string;
  detail: string;
  comoLlenarlo: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-hairline px-6 py-16 text-center">
      <span className="rounded-full border border-hairline bg-panel px-3 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-muted">
        Sin datos todavía
      </span>
      <span className="text-sm font-medium text-ink-strong">{title}</span>
      <p className="max-w-md text-xs leading-relaxed text-ink-faint">{detail}</p>
      <p className="max-w-md text-xs leading-relaxed text-ink-muted">{comoLlenarlo}</p>
    </div>
  );
}
